import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import {
	CreateOrganizationDto,
	OrganizationListRes,
	OrganizationRes,
	OrganizationType,
	ProgrammeDefaultsSummaryRes,
	UpdateOrganizationDto,
	WorkOsOrganizationLookupRes,
} from '@dealstack/shared/lib/generated';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';

export const listOrganizations = async (params: {
	page: number;
	limit: number;
	search?: string;
}) => {
	return makeRequest<ApiV1.Response<OrganizationListRes>>({
		endpoint: API_PATHS.ORGANIZATION_GET,
		method: HTTP_METHOD.GET,
		queryString: {
			page: params.page,
			limit: params.limit,
			...(params.search ? { search: params.search } : {}),
		},
	});
};

export const lookupWorkOsOrganizations = async (domain: string) => {
	return makeRequest<ApiV1.Response<WorkOsOrganizationLookupRes>>({
		endpoint: API_PATHS.ORGANIZATION_WORKOS_LOOKUP_GET,
		method: HTTP_METHOD.GET,
		queryString: { domain },
	});
};

export const getOrganizationById = async (organizationId: string) => {
	return makeRequest<ApiV1.Response<OrganizationRes | null>>({
		endpoint: formatUnicorn(API_PATHS.ORGANIZATION_ORGANIZATIONID_GET, {
			organizationId,
		}),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload ?? null);
};

export const createOrganization = async (body: CreateOrganizationDto) => {
	return makeRequest<ApiV1.Response<OrganizationRes>>({
		endpoint: API_PATHS.ORGANIZATION_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});
};

export const deleteOrganization = async (organizationId: string) => {
	return makeRequest<ApiV1.Response<OrganizationRes>>({
		endpoint: formatUnicorn(API_PATHS.ORGANIZATION_ORGANIZATIONID_DELETE, {
			organizationId,
		}),
		method: HTTP_METHOD.DELETE,
	});
};

export const importOrganization = async (authenticationProviderOrgId: string) => {
	return makeRequest<ApiV1.Response<OrganizationRes>>({
		endpoint: formatUnicorn(API_PATHS.ORGANIZATION_IMPORT_AUTHENTICATIONPROVIDERORGID_POST, {
			authenticationProviderOrgId,
		}),
		method: HTTP_METHOD.POST,
	});
};

export const updateOrganization = async ({
	id,
	body,
}: {
	id: string;
	body: UpdateOrganizationDto;
}) => {
	return await makeRequest<ApiV1.Response<OrganizationRes>>({
		endpoint: formatUnicorn(API_PATHS.ORGANIZATION_ORGANIZATIONID_PATCH, {
			organizationId: id,
		}),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
	});
};

export const updateOrganizationType = async ({
	organizationId,
	type,
}: {
	organizationId: string;
	type: OrganizationType | null;
}) => {
	return await makeRequest<ApiV1.Response<OrganizationRes>>({
		endpoint: formatUnicorn(API_PATHS.ORGANIZATION_ORGANIZATIONID_TYPE_PATCH, {
			organizationId,
		}),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify({ type }),
	});
};

export const deleteUserFromOrganization = async ({
	userId,
	organizationId,
}: {
	userId: string;
	organizationId: string;
}) => {
	const endpoint = formatUnicorn(API_PATHS.ORGANIZATION_ORGANIZATIONID_USERS_USERID_DELETE, {
		organizationId,
		userId,
	});
	return makeRequest({
		endpoint,
		method: HTTP_METHOD.DELETE,
	});
};

export const addOrgPermissionToUser = async ({
	organizationId,
	userId,
	permissionRoles,
}: {
	organizationId: string;
	userId: string;
	permissionRoles: PermissionRole[];
}) => {
	return makeRequest<void>({
		endpoint: formatUnicorn(
			API_PATHS.PERMISSIONS_RESOURCETYPE_RESOURCEID_PERMISSIONS_CREATE_POST,
			{
				resourceType: ResourceType.ORGANIZATION,
				resourceId: organizationId,
			}
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify({
			userId,
			permissionRoles,
		}),
	});
};

export const removeOrgPermissionFromUser = async ({
	organizationId,
	userId,
	permissionRoles,
}: {
	organizationId: string;
	userId: string;
	permissionRoles: PermissionRole[];
}) => {
	return makeRequest<void>({
		endpoint: formatUnicorn(
			API_PATHS.PERMISSIONS_RESOURCETYPE_RESOURCEID_PERMISSIONS_DELETE_POST,
			{
				resourceType: ResourceType.ORGANIZATION,
				resourceId: organizationId,
			}
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify({
			userId,
			permissionRoles,
		}),
	});
};

/**
 * The deal's lead Sponsor, resolved server-side from the `deal-id` header the API layer already
 * sends. Null when the deal has no organisation, or one that is not a Sponsor, so the offer is
 * skipped — only that organisation's defaults can be applied to the deal's programmes.
 */
export const getDealProgrammeDefaultsSummary = async () => {
	const response = await makeRequest<ApiV1.Response<ProgrammeDefaultsSummaryRes | null>>({
		endpoint: API_PATHS.PROGRAMME_DEFAULTS_SUMMARY_DEAL_GET,
		method: HTTP_METHOD.GET,
	});

	return response.payload ?? null;
};

/** The programme is read from the `mip-id` header the API layer already sends. */
export const applyProgrammeDefaults = async (organizationId: string) =>
	await makeRequest({
		endpoint: formatUnicorn(API_PATHS.PROGRAMME_DEFAULTS_SUMMARY_ORGANIZATIONID_APPLY_POST, {
			organizationId,
		}),
		method: HTTP_METHOD.POST,
	});
